// app/auth/auth.interface.ts
import { Session, User } from '@supabase/supabase-js';

import { Database } from '@/types/supabase';

// Use database types directly from Supabase
type Tables = Database['public']['Tables'];
export type UserProfile = Tables['user_profiles']['Row'];
export type Organization = Tables['organizations']['Row'];

// Create enriched organization type with user role info
export type EnrichedOrganization = Organization & {
  role?: string | null;
  is_primary?: boolean | null;
};



export interface SubscriptionStatus {
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid' | 'none' | 'free' | null;
  priceId: string | null;
  product_id?: string | null; // Added for product-based tier detection
  unit_amount?: number | null; // Added for price display
  currency?: string | null; // Added for price display
  interval?: string | null; // Added for price display
  interval_count?: number | null; // Added for price display
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean | null;
}

/**
 * Auth state stored in Zustand
 */
export interface AuthState {
  /** The authenticated user from Supabase */
  user: User | null;
  
  /** The current session from Supabase */
  session: Session | null;
  
  /** The user's profile from our database */
  profile: UserProfile | null;
  
  /** The user's organizations with role information */
  organizations: EnrichedOrganization[];
  
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  
  /** Whether the auth state is still loading */
  isLoading: boolean;
  
  /** Whether the user has completed onboarding */
  hasCompletedOnboarding: boolean;

  /** true once the first auth check finished */
  isAuthReady: boolean;

  subscription: SubscriptionStatus | null;

  isActiveSubscriber: boolean;

}

/**
 * Credentials for login
 */
export interface LoginCredentials {
  email: string;
  password: string;
}

/**
 * Registration data
 */
export interface SignupCredentials {
  email: string;
  password: string;
}

/**
 * Password reset credentials
 */
export interface ResetPasswordCredentials {
  email: string;
}

/**
 * Update password credentials
 */
export interface UpdatePasswordCredentials {
  password: string;
}

/**
 * Auth error codes
 */
export enum AuthErrorCode {
  INVALID_CREDENTIALS = 'invalid_credentials',
  EMAIL_ALREADY_EXISTS = 'email_already_exists',
  WEAK_PASSWORD = 'weak_password',
  USER_NOT_FOUND = 'user_not_found',
  NETWORK_ERROR = 'network_error',
  UNKNOWN_ERROR = 'unknown_error',
  NOT_FOUND = 'not_found',
  DATABASE_ERROR = 'database_error',
  TOO_MANY_ATTEMPTS = 'too_many_attempts',
  EXPIRED_CODE = 'expired_code',
  INVALID_CODE = 'invalid_code',
}

/**
 * Auth service response
 */
export interface AuthResponse<T = any> {
  data?: T;
  error?: {
    message: string;
    code?: AuthErrorCode;
  };
}

/**
 * User profile response
 */
export interface ProfileResponse {
  profile: UserProfile;
} 